import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
} from '@mui/material';
import { updateBusinessSummaryExpress } from 'lib/api/express';
import CustomButton from 'lib/designSystem/Button';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

interface GeneralEditDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: GeneralData) => void;
  initialData: GeneralData;
}

interface GeneralData {
  name?: string;
  taxIdentifier?: string;
  email?: string;
}

const GeneralEditDialog: React.FC<GeneralEditDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
}) => {
  const [formData, setFormData] = useState<GeneralData>(initialData);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    setFormData(initialData);
  }, [initialData]);

  const handleSave = async () => {
    if (!user?.clientId || !user?.businessIdentifier) {
      showAlert(
        dispatch,
        'error',
        'No se ha encontrado la información de la empresa',
      );
      return;
    }

    // Basic validation - email should be filled
    if (!formData.email || formData.email.trim() === '') {
      showAlert(dispatch, 'error', 'Debe completar el campo de email');
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showAlert(dispatch, 'error', 'Debe ingresar un email válido');
      return;
    }

    setLoading(true);
    try {
      await updateBusinessSummaryExpress(
        dispatch,
        user.clientId,
        user.businessIdentifier,
        { email: formData.email },
      );

      onSave(formData);
      showAlert(
        dispatch,
        'success',
        'Información general actualizada correctamente',
      );
      onClose();
    } catch (error) {
      console.error('Error updating general information:', error);
      showAlert(
        dispatch,
        'error',
        'Error al actualizar la información general',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ pt: 1 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Razón Social"
              value={formData.name ?? ''}
              variant="outlined"
              disabled
              helperText="Este campo no se puede editar"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="RUT"
              value={formData.taxIdentifier ?? ''}
              variant="outlined"
              disabled
              helperText="Este campo no se puede editar"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Email"
              value={formData.email ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              variant="outlined"
              placeholder="Ingrese el email de la empresa"
              type="email"
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <CustomButton
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          color="error"
        >
          Cancelar
        </CustomButton>
        <CustomButton onClick={handleSave} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default GeneralEditDialog;
